'use client'

import Link from 'next/link'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { Icon } from '@/components/icon'
import { Button } from '@/components/ui/button'
import type { PetaCampaignAttribution } from '@/features/marketing/peta-campaign/types'
import { capture } from '@/features/tracking'
import { PlayerIframe } from '@/features/video/player-iframe'
import { ShareButton } from '@/features/video/share-button.client'
import {
  TransitionInterceptBackTrigger,
  TransitionInterceptContext,
} from '@/lib/transitions/intercept'
import { useIframeCleanup } from './use-iframe-cleanup'

type WatchPlayerProps = {
  videoId: string
  videoTitle: string
  slug: string
  showHdUpsell?: boolean
  embedUrl?: string
  freeAccessLabel?: string | null
  freeAccessEndsAtIso?: string | null
  campaignAttribution?: PetaCampaignAttribution | null
  campaignHandoff?: boolean
}

export function WatchPlayer({
  videoId,
  videoTitle,
  slug,
  showHdUpsell,
  embedUrl,
  freeAccessLabel,
  freeAccessEndsAtIso,
  campaignAttribution,
  campaignHandoff = false,
}: WatchPlayerProps) {
  const interceptContext = useContext(TransitionInterceptContext)
  const shareUrl = `${typeof window !== 'undefined' ? window.location.origin : ''}/watch/${slug}`
  const embedIframeRef = useRef<HTMLIFrameElement>(null)
  const [_freeSecondsLeft, setFreeSecondsLeft] = useState<number | null>(null)
  const campaignAccessTracked = useRef(false)

  const isActivePath = useCallback((pathname: string) => pathname.startsWith('/watch/'), [])

  useIframeCleanup(embedIframeRef, { isActivePath })

  useEffect(() => {
    if (!campaignAttribution || campaignAccessTracked.current) return
    campaignAccessTracked.current = true

    if (campaignHandoff) {
      capture('campaign:watch_now_click', {
        ...campaignAttribution,
        cta_location: 'browse_handoff',
      })
    }

    capture('campaign:film_access', {
      ...campaignAttribution,
      content_id: videoId,
      quality: 'SD',
      auth_state: 'anonymous',
    })
  }, [campaignAttribution, campaignHandoff, videoId])

  useEffect(() => {
    if (!freeAccessEndsAtIso) {
      setFreeSecondsLeft(null)
      return
    }

    const endsAt = new Date(freeAccessEndsAtIso).getTime()
    if (!Number.isFinite(endsAt)) {
      setFreeSecondsLeft(null)
      return
    }

    const tick = () => {
      const deltaMs = endsAt - Date.now()
      setFreeSecondsLeft(Math.max(0, Math.ceil(deltaMs / 1000)))
    }

    tick()
    const interval = setInterval(tick, 1000)
    return () => clearInterval(interval)
  }, [freeAccessEndsAtIso])

  const backControl = (
    <Button variant='ghost' size='icon'>
      <Icon name='arrow' className='size-8 max-w-8' />
    </Button>
  )

  const trackPayItForwardClick = () => {
    capture('donation:pay_it_forward_modal_view', {
      trigger_point: 'player',
      ...campaignAttribution,
    })
  }

  const payItForwardLabel = (
    <span className='flex flex-col items-center leading-none'>
      <span>Pay It Forward</span>
      {showHdUpsell ? (
        <span className='mt-0.5 font-formula-semiextended text-[0.72rem] uppercase tracking-[0.08rem] opacity-70'>
          To watch in HD
        </span>
      ) : null}
    </span>
  )

  return (
    <div className='relative isolate min-h-svh bg-black'>
      <div className='absolute top-0 left-0 z-9999 w-full'>
        <div className='bg-linear-to-b from-0% from-black to-transparent p-4 text-christ-white-900'>
          <div className='flex flex-wrap items-start justify-between gap-x-8 gap-y-4'>
            <div className='flex min-w-0 flex-1 items-center gap-4 sm:gap-6'>
              {interceptContext ? (
                <TransitionInterceptBackTrigger asChild>
                  {backControl}
                </TransitionInterceptBackTrigger>
              ) : (
                <Button asChild variant='ghost' size='icon'>
                  {campaignAttribution ? (
                    <Link href='/' aria-label='Back to home'>
                      <Icon name='arrow' className='size-8 max-w-8' />
                    </Link>
                  ) : (
                    <Link href='/browse'>
                      <Icon name='arrow' className='size-8 max-w-8' />
                    </Link>
                  )}
                </Button>
              )}

              <div className='min-w-0 leading-none'>
                <span className='block font-semibold text-sm'>You&apos;re Watching</span>

                <h1 className='wrap-break-word mt-2 font-bold font-formula-condensed text-[1.8rem] uppercase sm:text-[2.25rem]'>
                  {videoTitle}
                </h1>
                {freeAccessEndsAtIso ? (
                  <span className='mt-1 block text-white/70 text-xs uppercase tracking-[0.08rem]'>
                    12 Days of Free Access • Dec 25–Jan 5
                  </span>
                ) : freeAccessLabel ? (
                  <span className='mt-1 block text-white/70 text-xs uppercase tracking-[0.08rem]'>
                    {freeAccessLabel}
                  </span>
                ) : null}
              </div>
            </div>

            <div className='flex w-full flex-wrap items-center justify-start gap-3 sm:w-auto sm:justify-end'>
              <div className='order-1 flex flex-col items-start gap-1 sm:order-2 sm:items-end'>
                <Button
                  asChild
                  className='h-auto overflow-visible whitespace-nowrap bg-christ-white-700 px-6 py-4 pb-3 text-center font-bold font-formula-condensed text-[1.25rem] text-christ-black-900 uppercase leading-none tracking-[-0.04rem] transition-all duration-300 hover:bg-christ-white-700 hover:opacity-90 sm:text-[1.5rem]'
                >
                  <Link href='/pay-it-forward' onClick={trackPayItForwardClick}>
                    {payItForwardLabel}
                  </Link>
                </Button>
              </div>

              <div className='order-2 sm:order-1'>
                <ShareButton url={shareUrl} />
              </div>
            </div>
          </div>
        </div>
      </div>
      {embedUrl ? (
        <iframe
          ref={embedIframeRef}
          title={`${videoTitle} trailer`}
          src={embedUrl}
          className='absolute inset-0 h-full w-full border-0 bg-black'
          loading='lazy'
          allow='accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture'
          allowFullScreen
        />
      ) : (
        <PlayerIframe videoId={videoId} />
      )}
    </div>
  )
}
